import { Flex, Tooltip, Typography } from "antd";
import { ColumnType } from "antd/es/table";
import { ReactComponent as DotIcon } from "images/Steps/dot.svg";
import {
  COMPANY_ROLES,
  COMPANY_STATUSES,
  COMPANY_TYPES,
  CompanyEntity,
  CompanyRole,
  getEnumKeys,
  REVERSE_ORDER_MAP,
} from "interfaces";
import { SquareIcon, Status } from "ui";
import { dateFormat, getFilterIcon, getSortIcon } from "utils";
import { CompanyList } from "stores/CompanyList";

import styles from "./columns.module.css";

import { ReactComponent as ProviderIcon } from "images/other/money.svg";
import { ReactComponent as CustomerIcon } from "images/menuIcons/Companies.svg";
import { ReactComponent as OperatorIcon } from "images/headerIcons/union.svg";

import { routes } from "constants/routes";

const ICON_COMPONENTS: Record<string, React.FunctionComponent<React.SVGProps<SVGSVGElement>>> = {
  LIQUIDITY_PROVIDER: ProviderIcon,
  CUSTOMER: CustomerIcon,
  OPERATOR: OperatorIcon,
};

export const getCompaniesColumns = ({
  companies,
  showFiltersToStatuses = true,
}: {
  companies: CompanyList;
  showFiltersToStatuses?: boolean;
}): ColumnType<CompanyEntity>[] => {
  return [
    {
      title: "Компания",
      dataIndex: "companyName",
      key: "companyName",
      width: 260,
      className: `text-clamp-ellipsis ${styles.companyName}`,
      sorter: { multiple: 3 },
      sortOrder: companies.sorters?.companyName ? REVERSE_ORDER_MAP[companies.sorters?.companyName] : undefined,
      sortIcon: getSortIcon,
      render: (_, record) => (
        <Flex vertical gap={2}>
          <Tooltip trigger="hover" placement="topLeft" title={record.companyName}>
            <span>{record.companyName}</span>
          </Tooltip>
          <Flex align="center" gap="small">
            <Typography.Text className={styles.statusDateText}>ИНН: {record.inn}</Typography.Text>
            <DotIcon className={styles.statusDateText} />
            <Typography.Text className={styles.statusDateText}>
              {record.type === COMPANY_TYPES.IP ? "ОГРНИП:" : "ОГРН:"} {record.companyRegistrationNumber}
            </Typography.Text>
          </Flex>
        </Flex>
      ),
    },

    {
      dataIndex: "role",
      key: "roles",
      title: "Роль",
      width: 160,
      filterMultiple: true,
      filterIcon: (filtered: boolean) => getFilterIcon(filtered),
      defaultFilteredValue: companies.queryParams["roles"],
      filtered: "roles" in companies.queryParams,
      filters: getEnumKeys(COMPANY_ROLES.texts).map((key) => ({
        text: COMPANY_ROLES.texts[key],
        value: key,
      })),

      render: (role: CompanyRole) => {
        return (
          <Flex align="center" gap="small">
            <SquareIcon icon={ICON_COMPONENTS[role]} size="xl" squareSize="3xl" squareBg />
            <Typography.Text>{COMPANY_ROLES.texts[role]}</Typography.Text>
          </Flex>
        );
      },
    },

    showFiltersToStatuses
      ? {
          dataIndex: "statusDate",
          key: "statuses",
          title: "Статус",
          align: "right",
          sorter: { multiple: 3 },
          sortOrder: companies.sorters?.statusDate ? REVERSE_ORDER_MAP[companies.sorters?.statusDate] : undefined,
          sortIcon: getSortIcon,
          filterMultiple: true,
          filterIcon: (filtered: boolean) => getFilterIcon(filtered),
          defaultFilteredValue: companies.queryParams["statuses"],
          filtered: "status" in companies.queryParams,
          filters: getEnumKeys(COMPANY_STATUSES.texts).map((key) => ({
            text: COMPANY_STATUSES.texts[key],
            value: key,
          })),
          width: 160,
          render: (_: any, record: CompanyEntity) => (
            <Flex vertical gap={2} align="end">
              <Status
                color={COMPANY_STATUSES.colors[record.status]}
                tooltip={`Обновлен ${dateFormat(record.statusDate, true)}`}
              >
                {COMPANY_STATUSES.texts[record.status]}
              </Status>
              <Typography.Text className={styles.statusDateText}>c {dateFormat(record.statusDate)}</Typography.Text>
            </Flex>
          ),
        }
      : {
          dataIndex: "statusDate",
          key: "statuses",
          title: "Статус",
          align: "right",
          sorter: { multiple: 3 },
          sortOrder: companies.sorters?.statusDate ? REVERSE_ORDER_MAP[companies.sorters?.statusDate] : undefined,
          sortIcon: getSortIcon,
          width: 160,

          render: (_: any, record: CompanyEntity) => (
            <Flex vertical gap={2} align="end">
              <Status
                color={COMPANY_STATUSES.colors[record.status]}
                tooltip={`Обновлен ${dateFormat(record.statusDate, true)}`}
              >
                {COMPANY_STATUSES.texts[record.status]}
              </Status>
              <Typography.Text className={styles.statusDateText}>c {dateFormat(record.statusDate)}</Typography.Text>
            </Flex>
          ),
        },
  ];
};
